import type { ControllerSession } from '../controller';
import { DEFAULT_ICE_SERVERS } from '../../shared/ice';
import { FILE_CHANNEL, FILE_RESUME_GRACE_MS } from '../../shared/filexfer';
import { getWebRtcConnectionSnapshot } from '../../shared/rtc-path';
import { DATA_CHANNEL_PROTOCOL_VERSION } from '../../shared/datachannel-guard';

const NO_DATA_RESPONSE_MS = 30_000;
const CONNECTION_ACTIVITY_POLL_MS = 1_000;
const STALLED_RECONNECT_DELAY_MS = 2_000;
const CONTROL_HEARTBEAT_INTERVAL_MS = 10_000;
const CONTROL_HEARTBEAT_TIMEOUT_MS = 28_000;
const PAIR_WAIT_TIMEOUT_MS = 15_000;
const REMOTE_VIDEO_MUTE_GRACE_MS = 8_000;

function controlPlaneHealthy(session: ControllerSession, now = Date.now()): boolean {
  // 仅观看不需要控制通道；控制模式下，画面继续刷新也不能掩盖
  // 鼠标键盘通道已经关闭或心跳失联，否则会出现“看起来在线但无法操作”。
  if (session.accessMode === 'view') return true;
  if (!session.controlReady || session.dc?.readyState !== 'open') return false;
  return session.controlHeartbeatAckAt <= 0 || now - session.controlHeartbeatAckAt < CONTROL_HEARTBEAT_TIMEOUT_MS;
}

export const controllerPeer = {
  async onWebRtcSignal(this: ControllerSession, data: {
    sdp?: RTCSessionDescriptionInit;
    candidate?: RTCIceCandidateInit;
  }): Promise<void> {
    // candidate 可能在连接已经建立后继续到达；只有初次 SDP 或“配对后
    // 先到的 candidate”才进入协商态，避免把稳定会话反复显示成连接中。
    if (this.connectionState.phase !== 'connected' && (data.sdp || this.connectionState.phase === 'paired')) {
      this.setConnectionState('negotiating', 'negotiating', '正在建立安全连接…', true);
    }
    if (!this.pc) {
      this.controlMessageGuard.reset();
      this.clipboardMessageGuard.reset();
      this.cursorMessageGuard.reset();
      this.fileMessageGuard.reset();
      this.pc = new RTCPeerConnection({
        iceServers: this.iceServers ?? DEFAULT_ICE_SERVERS,
      });
      const pc = this.pc;
      pc.onicecandidate = (ev) => {
        if (ev.candidate) this.sendSignal({ candidate: ev.candidate.toJSON() });
      };
      pc.onconnectionstatechange = () => {
        if (this.pc !== pc) return;
        const st = pc.connectionState;
        if (st === 'failed') {
          this.setConnectionState('recovering', 'network', '连接中断，正在恢复…', true);
          this.cb.onStatus('连接中断，尝试重连…', 'error');
          this.scheduleIceRestart(300);
        } else if (st === 'disconnected') {
          this.setConnectionState('recovering', 'network', '连接暂时不稳定，正在恢复…', true);
          this.scheduleIceRestart(1800);
        } else if (st === 'connected') {
          this.cancelIceRestart();
          this.reconnectAttempt = 0;
          this.markConnectionActivity();
          this.setConnectionState('connected', 'network', '连接已建立', true);
          this.cb.onStatus('已连接', 'online');
          void this.refreshConnection();
        }
      };
      pc.oniceconnectionstatechange = () => {
        if (this.pc !== pc) return;
        const iceState = pc.iceConnectionState;
        if (iceState === 'connected' || iceState === 'completed') {
          this.cancelIceRestart();
          void this.refreshConnection();
        }
        if (iceState === 'disconnected') {
          this.setConnectionState('recovering', 'network', '网络暂时不稳定，正在恢复…', true);
          this.scheduleIceRestart(1800);
        } else if (iceState === 'failed') {
          this.setConnectionState('recovering', 'network', '网络连接中断，正在恢复…', true);
          this.scheduleIceRestart(300);
        }
      };
      pc.ontrack = (ev) => {
        // 音频轨道可能先于视频轨道到达，不能因此结束“等待画面”状态。
        // 只有真正收到视频轨道，控制端才算具备可用的远程桌面画面。
        if (ev.track.kind !== 'video') return;
        this.bindRemoteVideoTrack(ev.track);
        if (!this.streamReady) {
          this.firstFrameMs = this.connectionStartedAt == null
            ? null
            : Math.max(0, Date.now() - this.connectionStartedAt);
          this.cb.onFirstFrame?.(this.firstFrameMs);
        }
        this.markVideoActivity();
        this.streamReady = true;
        this.stopPairWaitTimer();
        this.setConnectionState('connected', 'network', '正在接收画面', true);
        ev.track.enabled = this.videoEnabled;
        this.cb.onStream(ev.streams[0] || new MediaStream([ev.track]));
      };
      pc.ondatachannel = (ev) => {
        if (ev.channel.label === FILE_CHANNEL) {
          this.fileDc = ev.channel;
          this.setupFileChannel();
        } else if (ev.channel.label === 'clipboard') {
          this.clipboardDc = ev.channel;
          const channel = ev.channel;
          channel.onmessage = (e) => this.onClipboardMessage(e.data, channel);
          channel.onclose = () => {
            if (this.clipboardDc === channel) this.clipboardDc = null;
          };
        } else if (ev.channel.label === 'pointer') {
          this.pointerDc = ev.channel;
          const channel = ev.channel;
          channel.onclose = () => {
            if (this.pointerDc !== channel) return;
            this.pointerDc = null;
            if (!this.disposed) this.scheduleIceRestart(500);
          };
        } else if (ev.channel.label === 'wheel') {
          this.wheelDc = ev.channel;
          this.wheelDc.onclose = () => {
            if (this.wheelDc === ev.channel) this.wheelDc = null;
          };
        } else if (ev.channel.label === 'cursor') {
          this.cursorDc = ev.channel;
          const channel = ev.channel;
          channel.onmessage = (e) => this.onCursorMessage(e.data, channel);
          channel.onclose = () => {
            if (this.cursorDc === channel) this.cursorDc = null;
          };
        } else {
          const channel = ev.channel;
          this.dc = channel;
          channel.onmessage = (e) => this.onControl(e.data, channel);
          const updateControlState = (): void => {
            if (this.dc !== channel) return;
            this.controlReady = channel.readyState === 'open';
            if (this.controlReady) {
              this.markControlActivity();
              this.startControlHeartbeat();
              // 新 RTC 通道建立后先同步一次“当前仍按住”的键和鼠标键。
              // 这只是一条状态快照，不会把历史输入重新排队。
              this.sendInputState();
              this.setConnectionState('connected', 'network', '连接已建立', true);
            } else {
              this.stopControlHeartbeat();
            }
            this.cb.onControlReady?.(this.controlReady);
            if (!this.controlReady && !this.disposed) {
              this.setConnectionState('recovering', 'network', '连接正在恢复…', true);
              this.cb.onStatus('连接正在恢复…', 'error');
              this.scheduleIceRestart(500);
            }
          };
          channel.onopen = updateControlState;
          channel.onclose = updateControlState;
          channel.onerror = updateControlState;
          updateControlState();
        }
      };
      this.startConnectionActivityWatch();
      this.pathTimer = setInterval(() => void this.refreshConnection(), 1000);
    }

    const pc = this.pc;
    if (!pc) return;
    const generation = this.openGeneration;
    const isCurrent = (): boolean => !this.disposed && this.pc === pc && this.openGeneration === generation;

    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        if (!isCurrent()) return;
        if (data.sdp.type === 'offer') {
          const answer = await pc.createAnswer();
          if (!isCurrent()) return;
          await pc.setLocalDescription(answer);
          if (!isCurrent()) return;
          const localDescription = pc.localDescription;
          if (localDescription) this.sendSignal({ sdp: localDescription.toJSON() });
        }
        if (!isCurrent()) return;
        // Trickle ICE candidate 可能比 SDP 先到，等远端描述设置完成后再补加。
        const pending = this.pendingIceCandidates.splice(0);
        for (const candidate of pending) {
          await pc.addIceCandidate(candidate);
          if (!isCurrent()) return;
        }
      } else if (data.candidate) {
        if (!isCurrent()) return;
        if (!pc.remoteDescription) this.pendingIceCandidates.push(data.candidate);
        else {
          await pc.addIceCandidate(data.candidate);
          if (!isCurrent()) return;
        }
      }
    } catch (err) {
      console.error('[controller webrtc]', err);
      this.setConnectionState('recovering', 'negotiating', '连接协商失败，正在恢复…', true);
      this.cb.onStatus('连接协商失败，正在恢复…', 'error');
      this.scheduleReconnect();
    }
  },

  bindRemoteVideoTrack(this: ControllerSession, track: MediaStreamTrack): void {
    const previous = this.remoteVideoTrack;
    if (previous && previous !== track) {
      previous.onended = null;
      previous.onmute = null;
      previous.onunmute = null;
    }
    this.remoteVideoTrack = track;
    const markVideoStalled = (message: string): void => {
      if (this.disposed || this.remoteVideoTrack !== track) return;
      if (!this.connectionStalled) {
        this.connectionStalled = true;
        this.setConnectionState('stalled', 'timeout', message, true);
        this.cb.onConnectionStalled?.(true);
      }
      this.cb.onStatus(message, 'error');
      this.scheduleStalledReconnect();
    };
    track.onended = () => {
      if (this.videoTrackRecoveryTimer) {
        clearTimeout(this.videoTrackRecoveryTimer);
        this.videoTrackRecoveryTimer = null;
      }
      markVideoStalled('远端画面已停止，正在恢复…');
    };
    track.onmute = () => {
      if (this.disposed || this.remoteVideoTrack !== track || this.videoTrackRecoveryTimer) return;
      // mute 常常只是锁屏/切换显示器期间的瞬态状态，给被控端的同会话
      // 采集恢复留出时间；持续无画面才重建连接。
      this.videoTrackRecoveryTimer = setTimeout(() => {
        this.videoTrackRecoveryTimer = null;
        if (this.remoteVideoTrack === track && track.readyState === 'live' && track.muted) {
          markVideoStalled('远端画面暂时不可用，正在恢复…');
        }
      }, REMOTE_VIDEO_MUTE_GRACE_MS);
    };
    track.onunmute = () => {
      if (this.videoTrackRecoveryTimer) {
        clearTimeout(this.videoTrackRecoveryTimer);
        this.videoTrackRecoveryTimer = null;
      }
    };
  },

  async refreshConnection(this: ControllerSession): Promise<void> {
    const pc = this.pc;
    if (!pc || this.disposed || this.refreshingConnection) return;
    this.refreshingConnection = true;
    try {
      const snapshot = await getWebRtcConnectionSnapshot(
        pc,
        (this.dc?.bufferedAmount ?? 0) +
          (this.pointerDc?.bufferedAmount ?? 0) +
          (this.wheelDc?.bufferedAmount ?? 0) +
        (this.clipboardDc?.bufferedAmount ?? 0),
        this.lastTrafficSample,
        this.controlRttMs,
      );
      if (pc !== this.pc || this.disposed) return;
      const inboundBytes = snapshot.traffic.bytesReceived;
      const inboundFrames = snapshot.metrics.framesDecoded;
      const inboundBytesAdvanced =
        inboundBytes != null && this.lastInboundBytes != null && inboundBytes > this.lastInboundBytes;
      const inboundFramesAdvanced =
        inboundFrames != null && this.lastInboundFrames != null && inboundFrames > this.lastInboundFrames;
      if (inboundFramesAdvanced || (inboundBytesAdvanced && this.streamReady)) {
        this.markVideoActivity();
      } else if (inboundBytesAdvanced) {
        this.markConnectionActivity();
      }
      this.lastInboundBytes = inboundBytes;
      this.lastInboundFrames = inboundFrames;
      this.lastTrafficSample = snapshot.traffic;
      this.lastConnectionMetrics = snapshot.metrics;
      const signature = `${snapshot.path.kind}|${snapshot.path.label}|${snapshot.path.detail}`;
      if (signature !== this.lastPathSignature) {
        this.lastPathSignature = signature;
        console.info('[webrtc] connection path:', snapshot.path.label, snapshot.path.detail);
        this.cb.onConnectionPath?.(snapshot.path);
      }
      this.cb.onConnectionQuality?.(snapshot.metrics);
    } finally {
      this.refreshingConnection = false;
    }
  },

  startConnectionActivityWatch(this: ControllerSession): void {
    this.stopConnectionActivityWatch();
    this.lastInboundActivityAt = Date.now();
    this.connectionActivityTimer = setInterval(() => {
      if (this.disposed || !this.pc) return;
      // 浏览器切到后台后会主动节流定时器。后台期间不把“没有按时执行
      // 心跳/统计轮询”误报成断线；页面恢复时 recover() 会立即发起一次
      // 新心跳，再由真实回执决定是否需要重连。
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const connected = this.controlReady || this.pc.connectionState === 'connected';
      if (!connected) return;
      if (!this.lastInboundActivityAt) this.lastInboundActivityAt = Date.now();
      const now = Date.now();
      const controlRequired = this.accessMode !== 'view' && this.streamReady;
      const controlChannelStalled = controlRequired &&
        (!this.controlReady || this.dc?.readyState !== 'open');
      const controlHeartbeatStalled = controlRequired && this.controlReady &&
        this.dc?.readyState === 'open' && this.controlHeartbeatAckAt > 0 &&
        now - this.controlHeartbeatAckAt >= CONTROL_HEARTBEAT_TIMEOUT_MS;
      const controlStalled = controlChannelStalled || controlHeartbeatStalled;
      const videoStalled = this.streamReady && this.lastVideoActivityAt > 0 &&
        now - this.lastVideoActivityAt >= NO_DATA_RESPONSE_MS;
      const stalled = controlStalled || videoStalled || now - this.lastInboundActivityAt >= NO_DATA_RESPONSE_MS;
      if (stalled === this.connectionStalled) return;
      this.connectionStalled = stalled;
      if (stalled) {
        this.setConnectionState(
          'stalled',
          'timeout',
          controlChannelStalled ? '控制通道已断开，正在恢复…' :
            controlHeartbeatStalled ? '控制通道没有响应，正在恢复…' :
              videoStalled ? '远端画面没有更新，正在恢复…' : '连接暂时没有响应，正在恢复…',
          true,
        );
      }
      this.cb.onConnectionStalled?.(stalled);
      if (stalled) this.scheduleStalledReconnect();
    }, CONNECTION_ACTIVITY_POLL_MS);
  },

  startPairWaitTimer(this: ControllerSession): void {
    this.stopPairWaitTimer();
    this.pairWaitTimer = setTimeout(() => {
      this.pairWaitTimer = null;
      // 被控端只有在屏幕采集成功后才会发送第一份 SDP。即使 SDP 已经到达，
      // 仍要等到真正收到视频轨道；否则采集权限/编码失败会永久卡在等待页。
      if (this.disposed || !this.remotePeer || this.streamReady) return;
      this.cb.onStatus('远端画面启动超时，正在重新连接…', 'error');
      this.setConnectionState('stalled', 'timeout', '远端画面没有启动，正在恢复…', true);
      this.reconnectFromStall();
    }, PAIR_WAIT_TIMEOUT_MS);
  },

  stopPairWaitTimer(this: ControllerSession): void {
    if (!this.pairWaitTimer) return;
    clearTimeout(this.pairWaitTimer);
    this.pairWaitTimer = null;
  },

  stopConnectionActivityWatch(this: ControllerSession): void {
    if (this.connectionActivityTimer) {
      clearInterval(this.connectionActivityTimer);
      this.connectionActivityTimer = null;
    }
    this.lastInboundActivityAt = 0;
    this.lastControlActivityAt = 0;
    this.lastVideoActivityAt = 0;
    this.controlHeartbeatAckAt = 0;
    this.lastInboundBytes = null;
    this.lastInboundFrames = null;
    this.connectionStalled = false;
    if (this.stalledReconnectTimer) {
      clearTimeout(this.stalledReconnectTimer);
      this.stalledReconnectTimer = null;
    }
  },

  markConnectionActivity(this: ControllerSession): void {
    if (this.disposed) return;
    const now = Date.now();
    this.lastInboundActivityAt = now;
    if (!this.connectionStalled) return;
    const controlHealthy = controlPlaneHealthy(this, now);
    if (!controlHealthy) return;
    this.connectionStalled = false;
    if (this.stalledReconnectTimer) {
      clearTimeout(this.stalledReconnectTimer);
      this.stalledReconnectTimer = null;
    }
    this.cb.onConnectionStalled?.(false);
    this.setConnectionState('connected', 'network', '连接已恢复', true);
  },

  markControlActivity(this: ControllerSession): void {
    if (this.disposed) return;
    this.lastControlActivityAt = Date.now();
    this.markConnectionActivity();
  },

  markVideoActivity(this: ControllerSession): void {
    if (this.disposed) return;
    const now = Date.now();
    this.lastVideoActivityAt = now;
    this.lastInboundActivityAt = now;
    // 视频恢复不能掩盖一个已经失活的控制通道；只有控制面仍然健康时，
    // 视频活动才可以解除 stalled 状态。
    const controlHealthy = controlPlaneHealthy(this, now);
    if (!this.connectionStalled || !controlHealthy) return;
    this.connectionStalled = false;
    if (this.stalledReconnectTimer) {
      clearTimeout(this.stalledReconnectTimer);
      this.stalledReconnectTimer = null;
    }
    this.cb.onConnectionStalled?.(false);
    this.setConnectionState('connected', 'network', '连接已恢复', true);
  },

  startControlHeartbeat(this: ControllerSession): void {
    this.stopControlHeartbeat();
    this.controlHeartbeatAckAt = Date.now();
    const send = (): void => {
      const channel = this.dc;
      if (this.disposed || !channel || channel.readyState !== 'open') return;
      try {
        const ts = Date.now();
        this.controlHeartbeatSentAt = ts;
        channel.send(JSON.stringify({
          type: 'session-heartbeat',
          ts,
          protocolVersion: DATA_CHANNEL_PROTOCOL_VERSION,
        }));
        // 健康状态必须等对端回执确认；这里只保留首次发送时间，避免
        // “能发出去”被误判为“对方仍然可用”。
        this.lastControlActivityAt = this.lastControlActivityAt || ts;
      } catch {
        // 健康检查会在没有回执时触发重连。
      }
    };
    send();
    this.controlHeartbeatTimer = setInterval(send, CONTROL_HEARTBEAT_INTERVAL_MS);
  },

  stopControlHeartbeat(this: ControllerSession): void {
    if (this.controlHeartbeatTimer) {
      clearInterval(this.controlHeartbeatTimer);
      this.controlHeartbeatTimer = null;
    }
    this.controlHeartbeatAckAt = 0;
    this.controlHeartbeatSentAt = 0;
    this.controlRttMs = null;
    this.pendingInputAcks.clear();
    this.inputRttMs = null;
    this.cb.onInputRtt?.(null);
  },

  scheduleStalledReconnect(this: ControllerSession): void {
    if (this.disposed || this.stalledReconnectTimer) return;
    this.stalledReconnectTimer = setTimeout(() => {
      this.stalledReconnectTimer = null;
      if (!this.disposed && this.connectionStalled) this.reconnectFromStall();
    }, STALLED_RECONNECT_DELAY_MS);
  },

  reconnectFromStall(this: ControllerSession): void {
    if (this.disposed) return;
    const oldWs = this.ws;
    this.closePeerConnection(true);
    this.ws = null;
    this.stopHeartbeat();
    this.releasePair(oldWs, 'controller-timeout');
    this.remotePeer = null;
    this.cb.onPeerInfo?.(null);
    this.cb.onControlReady?.(false);
    this.setConnectionState('recovering', 'timeout', '连接长时间无响应，正在重新连接…', true);
    this.cb.onStatus('连接长时间无响应，正在重新连接…', 'error');
    try { oldWs?.close(); } catch { /* ignore */ }
    this.scheduleReconnect();
  },

  /** 页面恢复、网络恢复或浏览器从后台回来时主动检查连接。 */
  recover(this: ControllerSession): void {
    if (this.disposed) return;
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
      if (this.controlReady && this.dc?.readyState === 'open') this.startControlHeartbeat();
    }
    if (this.connectionStalled) {
      this.setConnectionState('recovering', 'network', '正在检查连接…', true);
      this.reconnectFromStall();
      return;
    }
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.setConnectionState('recovering', 'network', '正在恢复连接…', true);
      this.scheduleReconnect();
      return;
    }
    const pc = this.pc;
    if (!pc) return;
    if (
      pc.connectionState === 'failed' ||
      pc.iceConnectionState === 'failed' ||
      pc.iceConnectionState === 'disconnected'
    ) {
      this.scheduleIceRestart(0);
    }
  },

  cancelIceRestart(this: ControllerSession): void {
    if (this.iceRestartTimer) {
      clearTimeout(this.iceRestartTimer);
      this.iceRestartTimer = null;
    }
    if (this.iceRestartWatchdogTimer) {
      clearTimeout(this.iceRestartWatchdogTimer);
      this.iceRestartWatchdogTimer = null;
    }
  },

  scheduleIceRestart(this: ControllerSession, delay: number): void {
    if (this.disposed || !this.pc || this.iceRestartTimer || this.reconnectTimer) return;
    this.iceRestartTimer = setTimeout(() => {
      this.iceRestartTimer = null;
      void this.restart();
    }, delay);
  },

  async restart(this: ControllerSession): Promise<void> {
    const pc = this.pc;
    if (this.iceRestartInFlight || !pc || this.disposed) return;
    const generation = this.openGeneration;
    const isCurrent = (): boolean => !this.disposed && this.pc === pc && this.openGeneration === generation;
    if (pc.signalingState === 'closed') return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.scheduleReconnect();
      return;
    }
    if (pc.signalingState !== 'stable') {
      this.scheduleIceRestart(1200);
      return;
    }
    this.iceRestartInFlight = true;
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      if (!isCurrent()) return;
      await pc.setLocalDescription(offer);
      if (!isCurrent()) return;
      const localDescription = pc.localDescription;
      if (localDescription) this.sendSignal({ sdp: localDescription.toJSON() });
    } catch (err) {
      console.error('[controller ice restart]', err);
      this.setConnectionState('recovering', 'network', '连接协商失败，正在恢复…', true);
      this.scheduleReconnect();
    } finally {
      this.iceRestartInFlight = false;
    }
    // ICE restart 无法修复时，重新建立一套配对和 RTC 通道。
    const watchdog = setTimeout(() => {
      if (this.iceRestartWatchdogTimer === watchdog) this.iceRestartWatchdogTimer = null;
      if (
        this.iceRestartWatchdogTimer === null &&
        isCurrent() &&
        (pc.connectionState === 'failed' || pc.iceConnectionState === 'failed')
      ) {
        this.scheduleReconnect();
      }
    }, 5000);
    this.iceRestartWatchdogTimer = watchdog;
  },

  closePeerConnection(this: ControllerSession, preserveFileTransfer = false): void {
    this.stopPairWaitTimer();
    this.cancelIceRestart();
    this.iceRestartInFlight = false;
    this.stopControlHeartbeat();
    this.stopConnectionActivityWatch();
    // 使尚未完成的系统剪贴板写入失效，避免断开后旧内容写回新会话。
    this.clipboardReceiveRevision += 1;
    this.pendingIceCandidates = [];
    this.clearClipboardTransfers();
    this.clearFileClipboardTransfers();
    this.clearFileAckWaiters();
    this.clearFileResumeWaiters();
    if (this.pathTimer) {
      clearInterval(this.pathTimer);
      this.pathTimer = null;
    }
    this.lastTrafficSample = null;
    if (this.videoTrackRecoveryTimer) {
      clearTimeout(this.videoTrackRecoveryTimer);
      this.videoTrackRecoveryTimer = null;
    }
    if (this.remoteVideoTrack) {
      this.remoteVideoTrack.onended = null;
      this.remoteVideoTrack.onmute = null;
      this.remoteVideoTrack.onunmute = null;
      this.remoteVideoTrack = null;
    }
    this.streamReady = false;
    const channels = [this.dc, this.fileDc, this.pointerDc, this.wheelDc, this.cursorDc, this.clipboardDc];
    this.dc = null;
    this.fileDc = null;
    this.pointerDc = null;
    this.wheelDc = null;
    this.cursorDc = null;
    this.clipboardDc = null;
    this.clearFileReceiveTimeout();
    if (this.fileRecv && preserveFileTransfer) {
      // RTC 通道重建不会改变文件任务本身；保留已写入的数据一小段时间，
      // 新通道通过 resume-request 取得精确偏移后继续传输。
      this.fileReceiveResumeUntil = Date.now() + FILE_RESUME_GRACE_MS;
      this.armFileReceiveTimeout();
    } else {
      if (this.fileRecv) void this.cancelReceivedFileStream(this.fileRecv);
      this.fileRecv = null;
      this.fileReceiveResumeUntil = 0;
      this.fileClipboardBatch = null;
    }
    for (const channel of channels) {
      if (!channel) continue;
      channel.onopen = null;
      channel.onclose = null;
      channel.onerror = null;
      channel.onmessage = null;
      try {
        channel.close();
      } catch {
        // ignore
      }
    }
    const pc = this.pc;
    this.pc = null;
    if (pc) {
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      pc.ontrack = null;
      pc.ondatachannel = null;
      try {
        pc.close();
      } catch {
        // ignore
      }
    }
  },
};
